import { useState, useEffect, useRef } from "react";

// ── Design Tokens ──
const gold = "#f5a623";
const blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e";
const red = "#ef4444";
const purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

function FeatCard({ icon, title, body, accent = blue }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: navy3, border: `1px solid ${hov ? accent + "55" : border}`, borderRadius: 8, padding: "22px 20px", transition: "all 0.2s", transform: hov ? "translateY(-2px)" : "none" }}>
      <div style={{ fontSize: "1.4rem", marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{body}</div>
    </div>
  );
}

// ── Section 1: Fixed Income Types ──
const bondTypes = [
  {
    label: "🏛️ Government", color: blue2,
    items: [
      { name: "Federal Bonds", badge: "Highest Quality", badgeColor: green, desc: "Issued by the Government of Canada. Virtually no default risk — backed by the full faith of the federal government. Includes marketable bonds and T-bills." },
      { name: "Provincial Bonds", badge: "Second Tier", badgeColor: blue2, desc: "Issued by provincial governments. May be guaranteed by the province. Slightly higher yield than federal to compensate for marginally higher risk." },
      { name: "Municipal — Instalment Debentures", badge: "Serial Bonds", badgeColor: gold, desc: "A portion of the principal matures every year to match the depreciation of the funded project (sewers, roads). Also called serial bonds." },
    ],
  },
  {
    label: "🏢 Corporate", color: gold,
    items: [
      { name: "Secured Bonds", badge: "Collateralized", badgeColor: green, desc: "Backed by specific assets pledged as collateral. If the issuer defaults, bondholders can seize the pledged assets. Lower risk → lower yield." },
      { name: "Debentures", badge: "Unsecured", badgeColor: gold, desc: "Backed only by the issuer's general creditworthiness — no specific collateral. Higher risk → higher yield than secured bonds." },
      { name: "Convertible Bonds", badge: "Equity Option", badgeColor: purple, desc: "Can be exchanged for common shares at a specific conversion price. Offers bond safety + equity upside. Trades at a premium to straight bonds." },
      { name: "Callable Bonds", badge: "Issuer Right", badgeColor: red, desc: "Issuer can repay the bond early (\"call\" it). Usually exercised when rates fall, allowing cheaper refinancing. Disadvantage to the investor — reinvestment risk." },
    ],
  },
  {
    label: "⏱️ Money Market", color: green,
    items: [
      { name: "Treasury Bills (T-Bills)", badge: "< 1 Year", badgeColor: green, desc: "Short-term federal debt. Sold at a discount, mature at par. No regular coupon — return is the spread between purchase price and par value. Taxed as interest income." },
      { name: "Commercial Paper", badge: "Corporate ST", badgeColor: gold, desc: "Unsecured short-term promissory notes issued by large corporations. Higher yield than T-bills due to credit risk. Typical maturity under 270 days." },
      { name: "Bankers' Acceptances (BAs)", badge: "Bank Guaranteed", badgeColor: blue2, desc: "Short-term commercial drafts guaranteed by a chartered bank. Lower yield than commercial paper due to bank backing. Used heavily in trade finance." },
    ],
  },
];

function BondTypeTabs() {
  const [activeTab, setActiveTab] = useState(0);
  const tab = bondTypes[activeTab];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {bondTypes.map((t, i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{ padding: "10px 18px", background: activeTab === i ? navy4 : navy3, border: `1px solid ${activeTab === i ? t.color + "44" : border}`, borderBottom: activeTab === i ? `2px solid ${t.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: activeTab === i ? t.color : muted, transition: "all 0.2s" }}>{t.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${tab.color}22`, borderRadius: "0 0 8px 8px", padding: "20px 22px" }}>
        <div style={{ display: "grid", gap: 10 }}>
          {tab.items.map((item, i) => (
            <div key={i} style={{ background: navy3, borderRadius: 8, padding: "16px 18px", display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white }}>{item.name}</div>
                  <span style={{ fontSize: "0.62rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, color: item.badgeColor, background: `${item.badgeColor}18`, border: `1px solid ${item.badgeColor}33`, letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{item.badge}</span>
                </div>
                <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Bond Price / Yield Relationship ──
function BondPriceViz() {
  const [rate, setRate] = useState(6);
  const coupon = 6;
  const diff = rate - coupon;
  const status = diff > 0 ? "discount" : diff < 0 ? "premium" : "par";
  const estPrice = Math.max(50, Math.min(150, 100 - diff * 8));

  const statusInfo = {
    discount: { label: "Discount", color: red, sub: "Price < 100", rule: "Yield > Coupon Rate", emoji: "📉" },
    premium: { label: "Premium", color: green, sub: "Price > 100", rule: "Yield < Coupon Rate", emoji: "📈" },
    par: { label: "At Par", color: gold, sub: "Price = 100", rule: "Yield = Coupon Rate", emoji: "⚖️" },
  }[status];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>📊 Bond Price ↔ Yield Relationship</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        This bond has a fixed coupon of <strong style={{ color: gold }}>6%</strong>. Drag the market interest rate to see how the price and yield relationship changes.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Market Rate</label>
        <input type="range" min={2} max={12} step={0.5} value={rate} onChange={e => setRate(parseFloat(e.target.value))} style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "0.95rem", color: gold, width: 50, textAlign: "right" }}>{rate}%</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Coupon Rate", value: `${coupon}%`, color: blue2, sub: "Fixed at issuance" },
          { label: "Market Rate", value: `${rate}%`, color: gold, sub: "Current environment" },
          { label: "Est. Bond Price", value: `~${estPrice.toFixed(0)}`, color: statusInfo.color, sub: statusInfo.sub },
        ].map((card, i) => (
          <div key={i} style={{ background: navy4, borderRadius: 8, padding: "14px 14px", textAlign: "center" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: card.color, marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.4rem", color: white }}>{card.value}</div>
            <div style={{ fontSize: "0.72rem", color: muted, marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${statusInfo.color}44`, borderRadius: 8, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontSize: "1.8rem" }}>{statusInfo.emoji}</span>
        <div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: statusInfo.color, marginBottom: 4 }}>Trading at {statusInfo.label} — {statusInfo.sub}</div>
          <div style={{ fontSize: "0.82rem", color: textCol }}>{statusInfo.rule}</div>
          <div style={{ fontSize: "0.8rem", color: muted, marginTop: 4 }}>
            {status === "discount" ? `Market rates (${rate}%) are above the coupon (${coupon}%), so investors demand a lower price to earn a competitive return.`
              : status === "premium" ? `Market rates (${rate}%) are below the coupon (${coupon}%), so investors pay extra for the above-market coupon payments.`
              : "Market rates equal the coupon — the bond trades at its face value."}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Yield Curve ──
function YieldCurve() {
  const [curve, setCurve] = useState("normal");
  const configs = {
    normal: {
      label: "Normal (Upward Sloping)", color: green,
      points: [3.2, 3.6, 4.0, 4.3, 4.6, 4.8, 5.0, 5.1, 5.2, 5.3],
      meaning: "Long-term rates > Short-term rates. The typical, healthy state. Investors demand a premium for lending money long-term (more uncertainty).",
      signal: "📈 Economic expansion expected. Normal risk/return relationship.",
    },
    inverted: {
      label: "Inverted (Downward Sloping)", color: red,
      points: [5.4, 5.1, 4.8, 4.5, 4.2, 4.0, 3.8, 3.7, 3.6, 3.5],
      meaning: "Short-term rates > Long-term rates. Historically one of the most reliable recession predictors. Investors expect rates to fall in the future.",
      signal: "⚠️ Historically signals a recession within 12–18 months.",
    },
    flat: {
      label: "Flat", color: gold,
      points: [4.5, 4.52, 4.54, 4.55, 4.56, 4.57, 4.58, 4.59, 4.60, 4.61],
      meaning: "Short and long-term rates are similar. Often a transition phase between normal and inverted. Can signal economic uncertainty.",
      signal: "🔄 Transition phase — watch for further movement.",
    },
  };
  const cfg = configs[curve];
  const maturities = ["3M", "6M", "1Y", "2Y", "3Y", "5Y", "7Y", "10Y", "20Y", "30Y"];
  const minY = Math.min(...cfg.points) - 0.3;
  const maxY = Math.max(...cfg.points) + 0.3;
  const chartH = 120, chartW = 520;

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>📉 Yield Curve Explorer</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20 }}>The yield curve plots bond yields against their maturities. Its shape tells us a lot about economic expectations.</p>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {Object.entries(configs).map(([key, val]) => (
          <button key={key} onClick={() => setCurve(key)} style={{ padding: "8px 16px", background: curve === key ? navy4 : navy3, border: `1px solid ${curve === key ? val.color + "55" : border}`, borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: curve === key ? val.color : muted, transition: "all 0.2s" }}>{val.label}</button>
        ))}
      </div>
      {/* SVG chart */}
      <div style={{ overflowX: "auto", marginBottom: 14 }}>
        <svg width={chartW} height={chartH + 40} style={{ display: "block" }}>
          {/* grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
            const y = chartH * pct;
            return <line key={i} x1={30} x2={chartW - 10} y1={y + 10} y2={y + 10} stroke={border} strokeWidth={1} />;
          })}
          {/* y axis labels */}
          {[maxY, (maxY + minY) / 2, minY].map((val, i) => (
            <text key={i} x={4} y={i === 0 ? 18 : i === 1 ? chartH / 2 + 14 : chartH + 8} fontSize={9} fill={muted}>{val.toFixed(1)}%</text>
          ))}
          {/* curve line */}
          <polyline
            points={cfg.points.map((v, i) => {
              const x = 30 + i * ((chartW - 40) / (cfg.points.length - 1));
              const y = 10 + chartH - ((v - minY) / (maxY - minY)) * chartH;
              return `${x},${y}`;
            }).join(" ")}
            fill="none" stroke={cfg.color} strokeWidth={2.5} strokeLinejoin="round"
          />
          {/* dots */}
          {cfg.points.map((v, i) => {
            const x = 30 + i * ((chartW - 40) / (cfg.points.length - 1));
            const y = 10 + chartH - ((v - minY) / (maxY - minY)) * chartH;
            return <circle key={i} cx={x} cy={y} r={4} fill={cfg.color} />;
          })}
          {/* x axis labels */}
          {maturities.map((m, i) => {
            const x = 30 + i * ((chartW - 40) / (maturities.length - 1));
            return <text key={i} x={x} y={chartH + 28} fontSize={9} fill={muted} textAnchor="middle">{m}</text>;
          })}
        </svg>
      </div>
      <div style={{ background: navy4, border: `1px solid ${cfg.color}33`, borderRadius: 8, padding: "14px 18px" }}>
        <div style={{ fontSize: "0.82rem", color: textCol, lineHeight: 1.7, marginBottom: 6 }}>{cfg.meaning}</div>
        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: cfg.color }}>{cfg.signal}</div>
      </div>
    </div>
  );
}

// ── Volatility Explorer ──
function VolatilityExplorer() {
  const [maturity, setMaturity] = useState(20);
  const [coupon, setCoupon] = useState(3);
  // Higher maturity = more volatile; lower coupon = more volatile
  const score = Math.round((maturity / 30) * 50 + ((10 - coupon) / 10) * 50);
  const level = score >= 70 ? { label: "Very High", color: red } : score >= 50 ? { label: "High", color: "#f97316" } : score >= 30 ? { label: "Moderate", color: gold } : { label: "Low", color: green };

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>⚡ Bond Volatility Explorer</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Adjust maturity and coupon to understand which bonds move most when interest rates change. <strong style={{ color: textCol }}>Rule: Longest maturity + Lowest coupon = Highest volatility.</strong>
      </p>
      <div style={{ display: "grid", gap: 14, marginBottom: 24 }}>
        {[
          { label: "Time to Maturity (years)", val: maturity, set: setMaturity, min: 1, max: 30, unit: "yrs", tip: "Longer maturity → more volatile" },
          { label: "Coupon Rate (%)", val: coupon, set: setCoupon, min: 1, max: 10, unit: "%", tip: "Lower coupon → more volatile" },
        ].map((row, i) => (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>{row.label}</label>
              <span style={{ fontFamily: "monospace", fontSize: "0.85rem", color: gold }}>{row.val}{row.unit}</span>
            </div>
            <input type="range" min={row.min} max={row.max} value={row.val} onChange={e => row.set(parseInt(e.target.value))} style={{ width: "100%", accentColor: gold }} />
            <div style={{ fontSize: "0.72rem", color: muted, marginTop: 4 }}>{row.tip}</div>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${level.color}44`, borderRadius: 8, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted }}>Relative Volatility</div>
          <span style={{ fontSize: "0.75rem", fontWeight: 700, padding: "4px 12px", borderRadius: 100, color: level.color, background: `${level.color}18`, border: `1px solid ${level.color}33` }}>{level.label}</span>
        </div>
        <div style={{ height: 10, background: navy3, borderRadius: 5, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${score}%`, background: `linear-gradient(90deg,${green},${gold},${red})`, borderRadius: 5, transition: "width 0.4s ease" }} />
        </div>
        <div style={{ fontSize: "0.8rem", color: textCol, marginTop: 12, lineHeight: 1.7 }}>
          A <strong style={{ color: white }}>{maturity}-year bond with a {coupon}% coupon</strong> would experience {level.label.toLowerCase()} price movement if market rates shifted by 1%. {score >= 70 ? "This is a high-risk bond for interest rate exposure." : score >= 50 ? "Significant duration risk — suitable for investors with a longer horizon." : score >= 30 ? "Moderate sensitivity — balanced risk profile." : "Relatively stable price — suitable for conservative investors."}
        </div>
      </div>
    </div>
  );
}

// ── Section 3: Equity Securities ──
function EquityComparison() {
  const [active, setActive] = useState(0);
  const shares = [
    {
      label: "📊 Common Shares", color: blue2, name: "Common Shares",
      tagline: "True ownership. Highest risk, highest potential reward.",
      rows: [
        ["Voting Rights", "Yes — one vote per share on major corporate decisions"],
        ["Dividends", "Not guaranteed — declared at the discretion of the board"],
        ["Capital Appreciation", "Unlimited upside potential"],
        ["Bankruptcy Priority", "Last in line — residual claim after all creditors and preferred shareholders"],
        ["Risk Level", "Highest among securities — price volatility is significant"],
      ],
      pills: ["Voting Rights", "Residual Claim", "Capital Gains Potential"],
    },
    {
      label: "⚖️ Preferred Shares", color: gold, name: "Preferred Shares",
      tagline: "Hybrid security — bond-like income, equity classification.",
      rows: [
        ["Voting Rights", "Typically non-voting (except in special circumstances)"],
        ["Dividends", "Fixed dividend — must be paid before common shareholders receive anything"],
        ["Bankruptcy Priority", "Ahead of common shares, but behind all creditors and bondholders"],
        ["Cumulative Feature", "Unpaid dividends accumulate in arrears and must be paid before common dividends resume"],
        ["Callable Feature", "Issuer may buy back shares at a specified call price"],
        ["Participating Feature", "May receive extra dividends if company profits exceed a threshold"],
      ],
      pills: ["Fixed Dividend", "Senior to Common", "Hybrid Security", "Cumulative Arrears"],
    },
  ];
  const sh = shares[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2 }}>
        {shares.map((s, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 20px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? s.color + "44" : border}`, borderBottom: active === i ? `2px solid ${s.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? s.color : muted, transition: "all 0.2s" }}>{s.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${sh.color}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white, marginBottom: 4 }}>{sh.name}</div>
        <div style={{ fontSize: "0.82rem", color: muted, fontStyle: "italic", marginBottom: 16 }}>{sh.tagline}</div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {sh.rows.map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "11px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: sh.color, width: 130, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
        <div>{sh.pills.map((p, i) => <Pill key={i} text={p} color={sh.color} />)}</div>
      </div>
    </div>
  );
}

// ── Bankruptcy Priority Visual ──
function BankruptcyPriority() {
  const tiers = [
    { icon: "🏛️", label: "Secured Creditors", color: green, sub: "First claim — holds specific collateral" },
    { icon: "📄", label: "Unsecured Creditors / Debenture Holders", color: blue2, sub: "General creditors, no specific collateral" },
    { icon: "⚖️", label: "Preferred Shareholders", color: gold, sub: "Ahead of common, behind all creditors" },
    { icon: "📊", label: "Common Shareholders", color: red, sub: "Last in line — residual claimants" },
  ];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 12, textAlign: "center" }}>Priority in Bankruptcy (Highest → Lowest)</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {tiers.map((t, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${t.color}`, borderRadius: 8, padding: "14px 18px" }}>
            <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>{t.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white }}>{i + 1}. {t.label}</div>
              <div style={{ fontSize: "0.78rem", color: muted, marginTop: 2 }}>{t.sub}</div>
            </div>
            <div style={{ fontFamily: "monospace", fontSize: "0.68rem", color: t.color, background: `${t.color}18`, border: `1px solid ${t.color}33`, padding: "3px 10px", borderRadius: 4, flexShrink: 0 }}>Tier {i + 1}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section 4: Derivatives ──
function DerivativesPanel() {
  const [open, setOpen] = useState(null);
  const items = [
    {
      icon: "📞", name: "Call Option", color: green,
      tagline: "Right to BUY. Profits when market rises.",
      def: "Gives the holder the right (but not the obligation) to buy an underlying asset at a specified strike price before a set expiry date.",
      use: "Buy a Call when you believe the market will rise. Maximum loss is the premium paid; upside is theoretically unlimited.",
      example: "You buy a call on XYZ Corp stock with a strike of $50 for a $3 premium. If XYZ rises to $65, you profit $12/share ($65 − $50 − $3).",
      pills: ["Right to Buy", "Bullish Strategy", "Limited Downside"],
    },
    {
      icon: "📤", name: "Put Option", color: red,
      tagline: "Right to SELL. Profits when market falls.",
      def: "Gives the holder the right (but not the obligation) to sell an underlying asset at a specified strike price before a set expiry date.",
      use: "Buy a Put when you believe the market will fall. Used for hedging (protecting existing positions) or speculating on declines.",
      example: "You buy a put on XYZ Corp with a strike of $50 for a $3 premium. If XYZ falls to $35, you profit $12/share ($50 − $35 − $3).",
      pills: ["Right to Sell", "Bearish Strategy", "Hedging Tool"],
    },
    {
      icon: "🔮", name: "Futures Contract", color: blue2,
      tagline: "Obligation to buy/sell on a future date. Exchange-traded.",
      def: "A standardized contract obligating both parties to buy/sell an asset at a predetermined price on a specific future date. Unlike options, there is an obligation on both sides.",
      use: "Used by producers to lock in prices (hedging) or by speculators to profit from anticipated price movements. High leverage — small price moves produce large profits or losses.",
      example: "A wheat farmer sells wheat futures to lock in $6/bushel. If prices fall to $4, they're protected. If prices rise to $8, they've forfeited the upside.",
      pills: ["Exchange-Traded", "Standardized", "Obligation Both Sides", "Leverage"],
    },
    {
      icon: "🤝", name: "Forward Contract", color: purple,
      tagline: "Obligation to buy/sell on a future date. OTC-traded.",
      def: "Similar to futures but privately negotiated over-the-counter (OTC) between two parties. Terms are customizable — size, maturity, and underlying asset can be tailored.",
      use: "Common in currency and commodity markets where customization is needed. Counterparty risk is higher than exchange-traded futures (no central clearing).",
      example: "A Canadian company importing US goods agrees to buy USD at 1.35 CAD in 90 days — locking in their cost regardless of where the exchange rate moves.",
      pills: ["OTC / Customizable", "Counterparty Risk", "Obligation Both Sides"],
    },
  ];
  return (
    <div style={{ margin: "28px 0", display: "grid", gap: 10 }}>
      {items.map((item, i) => (
        <div key={i} onClick={() => setOpen(open === i ? null : i)}
          style={{ background: open === i ? navy4 : navy3, border: `1px solid ${open === i ? item.color + "55" : border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", transition: "all 0.2s" }}>
          <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: "1.4rem", flexShrink: 0 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: white }}>{item.name}</div>
                <div style={{ fontSize: "0.75rem", color: item.color, marginTop: 2 }}>{item.tagline}</div>
              </div>
            </div>
            <span style={{ color: open === i ? item.color : muted, fontSize: "1rem", display: "inline-block", transform: open === i ? "rotate(90deg)" : "none", transition: "transform 0.25s", flexShrink: 0 }}>›</span>
          </div>
          {open === i && (
            <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${border}` }}>
              <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                {[["Definition", item.def, item.color], ["How It's Used", item.use, blue2], ["Example", item.example, green]].map(([lbl, val, col], j) => (
                  <div key={j} style={{ background: navy3, borderRadius: 6, padding: "12px 16px" }}>
                    <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: col, marginBottom: 6 }}>{lbl}</div>
                    <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.7 }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14 }}>{item.pills.map((p, j) => <Pill key={j} text={p} color={item.color} />)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Calculators ──
function CalcBox({ title, formula, children }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>{title}</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 14px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>{formula}</div>
      {children}
    </div>
  );
}

function CalcInput({ label, value, onChange }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.92rem", outline: "none" }} />
    </div>
  );
}

function CalcResult({ label, value, steps }) {
  return (
    <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: white }}>{value}</div>
      {steps && <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0", borderTop: `1px solid ${border}`, paddingTop: 12 }}>
        {steps.map((step, i) => (
          <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
            <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
          </li>
        ))}
      </ul>}
    </div>
  );
}

function CurrentYieldCalc() {
  const [fv, setFv] = useState(1000), [coupon, setCoupon] = useState(6), [price, setPrice] = useState(98);
  const [result, setResult] = useState(null);
  function calc() {
    const f = parseFloat(fv), c = parseFloat(coupon) / 100, p = parseFloat(price) / 100;
    const cashFlow = f * c;
    const mktPrice = f * p;
    const cy = (cashFlow / mktPrice * 100).toFixed(2);
    const prem = p > 1 ? "Premium" : p < 1 ? "Discount" : "Par";
    setResult({ cy, cashFlow, mktPrice, prem, fv: f, couponRate: coupon, priceIndex: price });
  }
  return (
    <CalcBox title="🧮 Current Yield Calculator" formula="Current Yield = (Annual Cash Flow / Current Market Price) × 100">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 16 }}>
        <CalcInput label="Face Value ($)" value={fv} onChange={setFv} />
        <CalcInput label="Coupon Rate (%)" value={coupon} onChange={setCoupon} />
        <CalcInput label="Bond Price (e.g. 98)" value={price} onChange={setPrice} />
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && <CalcResult label="Current Yield" value={`${result.cy}%`} steps={[
        `Annual coupon: $${result.fv.toLocaleString()} × ${result.couponRate}% = $${result.cashFlow.toLocaleString()}`,
        `Market price: ${result.priceIndex}% of $${result.fv.toLocaleString()} = $${result.mktPrice.toLocaleString()}`,
        `Current Yield: $${result.cashFlow} ÷ $${result.mktPrice} × 100 = ${result.cy}%`,
        `Bond is trading at: ${result.prem} (price ${result.priceIndex < 100 ? "<" : result.priceIndex > 100 ? ">" : "="} 100)`,
      ]} />}
    </CalcBox>
  );
}

function BondCostCalc() {
  const [fv, setFv] = useState(25000), [price, setPrice] = useState(104.5);
  const [result, setResult] = useState(null);
  function calc() {
    const f = parseFloat(fv), p = parseFloat(price);
    const cost = (p / 100) * f;
    const tag = p > 100 ? "Premium" : p < 100 ? "Discount" : "Par";
    setResult({ cost, f, p, tag });
  }
  return (
    <CalcBox title="🧮 Bond Purchase Cost Calculator" formula="Cost = (Face Value × Price Index) ÷ 100">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <CalcInput label="Face Value ($)" value={fv} onChange={setFv} />
        <CalcInput label="Bond Price (e.g. 104.5)" value={price} onChange={setPrice} />
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate Cost →</button>
      {result && <CalcResult label="Total Purchase Cost" value={`$${result.cost.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} steps={[
        `Price index: ${result.p} ÷ 100 = ${(result.p / 100).toFixed(4)}`,
        `Cost: ${(result.p / 100).toFixed(4)} × $${result.f.toLocaleString()} = $${result.cost.toLocaleString("en-CA", { minimumFractionDigits: 2 })}`,
        `Trading at: ${result.tag} (price ${result.p < 100 ? "below" : result.p > 100 ? "above" : "at"} par)`,
      ]} />}
    </CalcBox>
  );
}

function DivYieldCalc() {
  const [quarterly, setQuarterly] = useState(0.50), [mktPrice, setMktPrice] = useState(40);
  const [result, setResult] = useState(null);
  function calc() {
    const q = parseFloat(quarterly), p = parseFloat(mktPrice);
    const annual = q * 4;
    const dy = (annual / p * 100).toFixed(2);
    setResult({ annual, p, dy, q });
  }
  return (
    <CalcBox title="🧮 Preferred Share Dividend Yield" formula="Dividend Yield = (Annual Dividend / Current Market Price) × 100">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <CalcInput label="Quarterly Dividend ($)" value={quarterly} onChange={setQuarterly} />
        <CalcInput label="Market Price ($)" value={mktPrice} onChange={setMktPrice} />
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate Yield →</button>
      {result && <CalcResult label="Dividend Yield" value={`${result.dy}%`} steps={[
        `Annual dividend: $${result.q} × 4 quarters = $${result.annual.toFixed(2)}`,
        `Dividend Yield: $${result.annual.toFixed(2)} ÷ $${result.p} × 100 = ${result.dy}%`,
      ]} />}
    </CalcBox>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which of the following bonds would be the MOST volatile if interest rates rose by 1%?", opts: ["5-year maturity, 6% coupon", "20-year maturity, 6% coupon", "5-year maturity, 3% coupon", "20-year maturity, 3% coupon"], correct: 3, fb: { y: "Correct! Longest maturity (20 years) + lowest coupon (3%) = highest price volatility. Both factors amplify sensitivity to interest rate changes.", n: "Not quite. Volatility increases with longer maturity AND lower coupon. 20-year / 3% coupon has both factors working together — it is the most volatile." } },
  { q: "What is the primary difference between a 'Bond' and a 'Debenture'?", opts: ["Bonds are short-term; Debentures are long-term", "Bonds have specific assets pledged as collateral; Debentures are unsecured", "Debentures are issued by governments; Bonds by corporations", "Debentures can be converted to shares; Bonds cannot"], correct: 1, fb: { y: "Correct! A bond is secured by specific assets (collateral). A debenture is unsecured — backed only by the issuer's general creditworthiness.", n: "Not quite. The key distinction is security: bonds are backed by specific collateral; debentures rely only on the issuer's credit." } },
  { q: "XYZ Corp issues a bond with a 'Call Feature'. Who does this feature primarily benefit?", opts: ["The Investor", "The Issuer", "The Trustee", "The Government"], correct: 1, fb: { y: "Correct! The call feature benefits the issuer — it allows them to repay the bond early, typically when interest rates fall and cheaper refinancing is available.", n: "Not quite. Call features benefit the issuer. They create reinvestment risk for the investor — when bonds are called, investors must reinvest at lower prevailing rates." } },
  { q: "An investor purchases T-Bills. What type of income will they earn?", opts: ["Dividend Income", "Interest Income (taxed as income)", "Capital Gains", "Tax-free Income"], correct: 1, fb: { y: "Correct! T-Bills are sold at a discount and mature at par. The difference is classified and taxed as interest income — not capital gains.", n: "Not quite. The return on T-Bills (discount vs par) is classified as interest income, taxed at the full marginal rate." } },
  { q: "Which derivative instrument gives the holder the right, but not the obligation, to SELL a security at a set price?", opts: ["Call Option", "Put Option", "Futures Contract", "Forward Contract"], correct: 1, fb: { y: "Correct! A Put Option gives the right to sell. A Call Option gives the right to buy. Remember: Put = right to sell (bearish); Call = right to buy (bullish).", n: "Not quite. A Put Option is the right to sell. A Call Option is the right to buy. Futures and forwards are obligations, not rights." } },
  { q: "If the yield curve is 'Inverted', what does this typically signal?", opts: ["High economic growth ahead", "An impending recession", "Falling inflation only", "A bull market in stocks"], correct: 1, fb: { y: "Correct! An inverted yield curve (short-term rates > long-term rates) has historically been one of the most reliable leading indicators of a recession.", n: "Not quite. An inverted yield curve signals an impending recession — it means investors expect rates to fall in the future, usually because the economy will slow." } },
  { q: "Preferred shares are often described as 'hybrid' securities because:", opts: ["They trade on both the TSX and NYSE", "They have characteristics of both bonds (fixed payment) and equity (ownership)", "They convert automatically into cash at maturity", "They are only issued by financial companies"], correct: 1, fb: { y: "Correct! Preferred shares are hybrid securities — they pay a fixed dividend like a bond, but are technically equity. They rank ahead of common shares but behind all creditors.", n: "Not quite. Preferred shares are hybrids: fixed dividends (bond-like) + equity classification. They rank between bondholders and common shareholders." } },
  { q: "A 'Real Return Bond' protects the investor against:", opts: ["Default risk", "Liquidity risk", "Inflation risk", "Call risk"], correct: 2, fb: { y: "Correct! Real Return Bonds (RRBs) have their principal adjusted based on the CPI. As inflation rises, the principal increases, protecting purchasing power.", n: "Not quite. Real Return Bonds protect against inflation risk — the principal is linked to the CPI, so purchasing power is maintained regardless of inflation." } },
  { q: "A bond with a $1,000 face value has a 5% coupon and is trading at 95. What is its Current Yield?", opts: ["5.00%", "5.26%", "4.75%", "9.50%"], correct: 1, fb: { y: "Correct! Annual cash flow = $1,000 × 5% = $50. Market price = $950. Current Yield = $50 ÷ $950 × 100 = 5.26%.", n: "Not quite. Current Yield = Annual Cash Flow ÷ Market Price. $50 ÷ $950 = 5.26%. Because the bond is at a discount, current yield exceeds the coupon rate." } },
  { q: "If a bond is trading at a 'Premium', which of the following is TRUE?", opts: ["Current Yield > Coupon Rate", "Yield to Maturity > Coupon Rate", "The bond is trading below 100", "Coupon Rate > Yield to Maturity"], correct: 3, fb: { y: "Correct! At a premium (price > 100), investors are paying extra for above-market coupons, which means the effective yield they earn is lower than the stated coupon rate.", n: "Not quite. Premium means price > 100, meaning the coupon is higher than current market rates. Therefore the Coupon Rate > Yield to Maturity." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;

  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }

  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 7 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. Complete mastery of Chapter 7." : score >= 8 ? "Excellent. Review the questions you missed." : score >= 6 ? "Good foundation. Revisit bond pricing and derivatives." : "Go back through the volatility rules, yield curve, and option types."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Par Value", def: "The face value of a bond — the amount repaid at maturity (typically $1,000). A bond trading at 100 is trading at par." },
  { term: "Coupon Rate", def: "The annual interest rate paid by the bond issuer, fixed at issuance. Expressed as a % of face value." },
  { term: "Current Yield", def: "Annual cash flow divided by current market price. Reflects the actual return based on today's price, not face value." },
  { term: "Discount Bond", def: "A bond trading below par (price < 100). Occurs when market rates exceed the coupon rate. Current yield > coupon rate." },
  { term: "Premium Bond", def: "A bond trading above par (price > 100). Occurs when market rates are below the coupon rate. Coupon rate > current yield." },
  { term: "Debenture", def: "An unsecured bond backed only by the issuer's creditworthiness — no specific assets pledged as collateral." },
  { term: "Convertible Bond", def: "Can be converted into common shares at a specified price. Offers bond safety plus equity upside potential." },
  { term: "Callable Bond", def: "The issuer can redeem the bond before maturity, typically when rates fall. Creates reinvestment risk for the investor." },
  { term: "Treasury Bill (T-Bill)", def: "Short-term federal debt sold at a discount, maturing at par. No coupon — return is the price difference, taxed as interest." },
  { term: "Bankers' Acceptance (BA)", def: "A short-term commercial draft guaranteed by a chartered bank. Lower yield than commercial paper; used in trade finance." },
  { term: "Normal Yield Curve", def: "Upward sloping — long-term rates exceed short-term rates. The typical, healthy state reflecting inflation and duration risk premium." },
  { term: "Inverted Yield Curve", def: "Short-term rates exceed long-term rates. Historically a reliable leading indicator of an impending recession." },
  { term: "Volatility Rule", def: "Bond price sensitivity to rate changes increases with longer maturity and lower coupon. Longest maturity + lowest coupon = most volatile." },
  { term: "Common Share", def: "Ownership in a corporation. Voting rights, residual claim in bankruptcy (last), dividends at board discretion." },
  { term: "Preferred Share", def: "Hybrid security — fixed dividend (bond-like) + equity classification. Ranks ahead of common shares but behind all creditors." },
  { term: "Cumulative Preferred", def: "Unpaid dividends accumulate in arrears and must all be paid before common shareholders receive any dividend." },
  { term: "Call Option", def: "The right (not obligation) to buy an asset at a set strike price before expiry. Profits when the underlying price rises." },
  { term: "Put Option", def: "The right (not obligation) to sell an asset at a set strike price before expiry. Profits when the underlying price falls." },
  { term: "Futures Contract", def: "Standardized, exchange-traded obligation to buy/sell an asset at a future date and price. Both parties are obligated." },
  { term: "Forward Contract", def: "OTC, customizable obligation to buy/sell at a future date and price. Higher counterparty risk than exchange-traded futures." },
];

// ── Main Export ──
export default function Chapter7() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Fixed-Income Securities — Types & Features"],
    ["02", "Bond Pricing, Volatility & the Yield Curve"],
    ["03", "Equity Securities — Common & Preferred Shares"],
    ["04", "Derivatives — Options, Futures & Forwards"],
    ["🧮", "Current Yield, Bond Cost & Dividend Yield Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* HERO */}
        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 7
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Securities <span style={{ color: gold }}>&amp; Markets</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Fixed-income instruments, bond pricing mechanics, the yield curve, equity securities, and derivatives — with interactive visualizers, volatility tools, and three practice calculators.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","4","Core Topics"],["⏱","~20 min","read"],["🎯","10","practice questions"],["🧮","3","calculators"],["📈","2","interactive visualizers"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* TOC */}
        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1: Fixed Income ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>Fixed-Income</span><div style={s.line} /></div>
            <h2 style={s.h2}>Fixed-Income Securities</h2>
            <p style={s.prose}>
              Fixed-income securities represent <span style={{ color: gold, fontWeight: 600 }}>loans made by investors to issuers</span>. The issuer promises to repay the principal (par value) at maturity and typically pays regular interest (coupons) during the term. They range from the safest instruments in existence (Government of Canada bonds) to higher-risk corporate debentures.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, margin: "0 0 24px" }}>
              <FeatCard icon="💵" title="Par Value (Face Value)" body="The amount repaid at maturity — typically $1,000 or $10,000. A bond 'at par' trades at 100% of face value." />
              <FeatCard icon="📅" title="Coupon Rate" body="The annual interest rate fixed at issuance. Paid semi-annually. Doesn't change — market yields change around it." />
              <FeatCard icon="📆" title="Maturity Date" body="When the loan expires and principal is returned. Ranges from 30 days (T-bills) to 30+ years (long bonds)." />
            </div>
          </Reveal>
          <Reveal delay={0.2}><BondTypeTabs /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2: Bond Pricing ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Pricing & Volatility</span><div style={s.line} /></div>
            <h2 style={s.h2}>Bond Pricing, Volatility &amp; the Yield Curve</h2>
            <p style={s.prose}>
              Bond prices fluctuate in the secondary market based on changes in interest rates. Understanding the <span style={{ color: gold, fontWeight: 600 }}>inverse relationship</span> between prices and yields — and knowing which bonds are most sensitive — is one of the most tested concepts in Chapter 7.
            </p>
          </Reveal>
          <Reveal delay={0.1}><BondPriceViz /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⚡">
              <strong>The Two Volatility Rules — Memorize These:</strong><br />
              1. <strong>Maturity:</strong> Longer-term bonds are more volatile than short-term bonds.<br />
              2. <strong>Coupon:</strong> Lower-coupon bonds are more volatile than high-coupon bonds.<br />
              <strong>Exam shortcut:</strong> The most volatile bond is always the one with the <strong>longest maturity AND the lowest coupon</strong>.
            </Callout>
          </Reveal>
          <Reveal delay={0.3}><VolatilityExplorer /></Reveal>
          <Reveal delay={0.4}><YieldCurve /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3: Equities ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 03</span><span style={s.badge}>Equity Securities</span><div style={s.line} /></div>
            <h2 style={s.h2}>Equity Securities</h2>
            <p style={s.prose}>
              Equities represent <span style={{ color: gold, fontWeight: 600 }}>ownership in a corporation</span>. Unlike bonds, they have no maturity date and no guaranteed return. There are two main classes, with very different risk/return profiles and priorities in bankruptcy:
            </p>
          </Reveal>
          <Reveal delay={0.1}><EquityComparison /></Reveal>
          <Reveal delay={0.2}>
            <p style={{ ...s.prose, marginBottom: 16 }}>When a company is wound up, claims are paid out in strict priority order:</p>
            <BankruptcyPriority />
          </Reveal>
          <Reveal delay={0.3}>
            <Callout icon="⚖️" accent={gold}>
              <strong>Preferred Share Features — Know All Four:</strong> <strong>Cumulative</strong> — unpaid dividends accumulate and must clear before common dividends resume. <strong>Callable</strong> — issuer can redeem at will (benefits issuer, not investor). <strong>Participating</strong> — may get extra dividends if profits exceed a threshold. <strong>Convertible</strong> — can be converted into common shares.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S4: Derivatives ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 04</span><span style={s.badge}>Derivatives</span><div style={s.line} /></div>
            <h2 style={s.h2}>Derivatives</h2>
            <p style={s.prose}>
              Derivatives are instruments whose value is <span style={{ color: gold, fontWeight: 600 }}>derived from an underlying asset</span> — a stock, index, commodity, or currency. They are used for <strong style={{ color: white }}>hedging</strong> (reducing risk) or <strong style={{ color: white }}>speculation</strong> (increasing risk for profit). Click each instrument to see the full profile:
            </p>
          </Reveal>
          <Reveal delay={0.1}><DerivativesPanel /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="🎯" accent={blue}>
              <strong style={{ color: blue2 }}>Options Quick Reference:</strong> <strong style={{ color: green }}>Call Option</strong> = right to BUY → use when bullish (market rising). <strong style={{ color: red }}>Put Option</strong> = right to SELL → use when bearish (market falling). Both give a right, not an obligation. Futures and forwards are obligations on both sides. Futures trade on exchanges; forwards are OTC.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Calculators ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Interactive Tools</span><span style={s.badge}>Formulas & Practice</span><div style={s.line} /></div>
            <h2 style={s.h2}>Three Chapter 7 Calculators</h2>
            <p style={s.prose}>The three core calculations from Chapter 7 — all with step-by-step working that mirrors the exam approach.</p>
          </Reveal>
          <Reveal delay={0.1}><CurrentYieldCalc /></Reveal>
          <Reveal delay={0.2}><BondCostCalc /></Reveal>
          <Reveal delay={0.3}><DivYieldCalc /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering all four sections of Chapter 7. Detailed feedback on every answer.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 7 — bonds, equities, and derivatives.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
